<template>
    <div v-if="trip" class="pre-selection-airport">
        <FormV2
            id="form_transfer_pre_search"
            :key="formId"
            :onSubmit="(values) => handleSubmit(values as FormValues)"
            :initialValues="initialValues"
            :validationSchema="validationSchema">
            <Requirement :trip="trip" :onScroll="onClose" />

            <Notification v-if="description" :tier="1" :title="description.title">
                {{ description.text }}
            </Notification>

            <div class="pre-selection-airport-fields">
                <component
                    v-for="field in fields"
                    :key="field.name"
                    :is="field.component"
                    :name="field.name"
                    v-model="field.value"
                    :input="{ label: t({ key: field.labelKey }), items: field.options }" />
            </div>

            <Notification v-if="messageWarning" variant="warning" icon="InfoCircle">
                {{ messageWarning }}
            </Notification>

            <Notification
                v-if="messageInvalid"
                variant="negative"
                icon="InfoCircle"
                :title="t({ key: 'shared.check_pickup_time' })">
                {{ messageInvalid }}
            </Notification>

            <div class="pre-selection-airport-footer">
                <ButtonPrimary type="submit" :disabled="disabled">
                    {{ t({ key: 'shared.search' }) }}
                </ButtonPrimary>
            </div>
        </FormV2>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { defineComponent, ref, watch, computed, PropType } from 'vue';
    import {
        TransferPickupValidateResponse,
        TransferValidateType,
        TransferTrip,
        TransferProductType,
        TransferSearchCondition,
        TransferPickupConditionDescription,
    } from '@/helpers/transfer/transfer.interfaces';
    import {
        Validation,
        ValidationSchema,
    } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import {
        isTime,
        isDate,
        isFlightNumber,
        isDefined,
    } from '@ui/components/FormV2/Validations/functions';
    import { generateUUID } from '@shared/utils/uuid';
    import {
        TransferFlightTimeKey,
        TransferConnectionObject,
        SearchLocationType,
    } from '@/api/Transfer/transfer.interfaces';
    import { getAirportsOptions } from '@/helpers/transfer/transfer';
    import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
    import { getTransferSearchConditions } from '@/helpers/transfer/transfer.conditions';
    import {
        addTimeToDate,
        convertToDate,
        getShortDate,
        getTime,
    } from '@shared/helpers/datetime/datetime';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import TimeField from '@ui/components/FormV2/Fields/Time/TimeField.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import Requirement from '../../Shared/Requirement.vue';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import SelectField from '@ui/components/FormV2/Fields/Select/SelectField.vue';

    interface FormField {
        name: keyof FormValues;
        labelKey: string;
        value: any;
        validation: Validation[];
        component: any;
        options?: SelectItem[];
    }

    interface FormValues {
        Iata: string | undefined;
        Date: string | undefined;
        FlightTime: string | undefined;
        FlightNumber: string | undefined;
    }

    interface FlightData {
        time: TransferFlightTimeKey;
        connection: TransferConnectionObject;
    }

    export default defineComponent({
        name: 'TransferPreSelectionCustom',
        methods: { t },
        components: {
            FormV2,
            TimeField,
            DateInputField,
            TextboxField,
            Notification,
            ButtonPrimary,
            Requirement,
        },
        props: {
            trip: { type: Object as PropType<TransferTrip>, required: true },
            onSearch: { type: Function, required: true },
            onClose: { type: Function, required: true },
        },
        setup(props) {
            const store = useStore();
            const formId = ref<string | undefined>(undefined);

            const isArrival = computed<boolean>(
                () => props.trip.from.productType === TransferProductType.Airport
            );

            const flightData = computed<FlightData>(() => ({
                time: isArrival.value ? 'flightArrivalTime' : 'flightDepartureTime',
                connection: isArrival.value ? 'from' : 'to',
            }));

            const airports = computed<SelectItem[]>(() => {
                const airports =
                    props.trip[flightData.value.connection].closestAirports ?? [];

                return getAirportsOptions(airports);
            });

            const fieldIata = ref<string | undefined>(
                props.trip[flightData.value.connection].location.iata
            );

            const flightTime = computed<string | null>(
                () => props.trip.request[flightData.value.time]
            );

            const fieldDate = ref<string>(
                flightTime.value ? getShortDate(convertToDate(flightTime.value)) : ''
            );

            const fieldFlightTime = ref<string>(
                flightTime.value ? getTime(convertToDate(flightTime.value)) : ''
            );

            const fieldFlightNumber = ref<string | undefined>(
                isArrival.value ? props.trip.from.flightNumber : props.trip.to.flightNumber
            );

            const validateResponse = ref<TransferPickupValidateResponse | undefined>(
                undefined
            );

            const initialValues = ref<FormValues | undefined>(undefined);
            const validationSchema = ref<ValidationSchema | undefined>(undefined);

            const fields = computed<FormField[]>(() => getFields());

            const conditions = computed<TransferSearchCondition | undefined>(() =>
                getTransferSearchConditions(props.trip)
            );

            const description = computed<TransferPickupConditionDescription | undefined>(
                () => conditions.value?.description
            );

            const messageWarning = computed<string | undefined>(() => {
                if (validateResponse?.value?.type === TransferValidateType.Warning) {
                    return validateResponse?.value?.message;
                }

                return undefined;
            });

            const messageInvalid = computed<string | undefined>(() => {
                if (validateResponse?.value?.type === TransferValidateType.Invalid) {
                    return validateResponse?.value?.message;
                }

                return undefined;
            });

            const disabled = computed<boolean>(
                () => validateResponse.value?.type === TransferValidateType.Invalid
            );

            function setInitialState(fields: FormField[]) {
                initialValues.value = setInitialValues(fields);
                validationSchema.value = setValidationSchema(fields);

                // We do this to reinitialize the form due to intialValues / validationSchema changes
                formId.value = generateUUID();
            }

            function getFields(): FormField[] {
                const fields: FormField[] = [
                    {
                        name: 'Iata',
                        labelKey: 'shared.airport',
                        component: SelectField,
                        value: fieldIata.value,
                        validation: [isDefined],
                        options: airports.value,
                    },
                    {
                        name: 'Date',
                        labelKey: isArrival.value
                            ? 'shared.flight_arrival_date'
                            : 'shared.flight_departure_date',
                        component: DateInputField,
                        value: fieldDate.value,
                        validation: [isDate],
                    },
                    {
                        name: 'FlightTime',
                        labelKey: isArrival.value
                            ? 'shared.flight_arrival_time'
                            : 'shared.flight_departure_time',
                        component: TimeField,
                        value: fieldFlightTime.value,
                        validation: [isTime],
                    },
                    {
                        name: 'FlightNumber',
                        labelKey: 'shared.flight_number',
                        component: TextboxField,
                        value: fieldFlightNumber.value,
                        validation: [isFlightNumber],
                    },
                ];

                return fields;
            }

            function setValidationSchema(fields: FormField[]) {
                const schema: ValidationSchema = {};

                fields?.forEach((f) => {
                    if (f) {
                        schema[f.name] = f.validation;
                    }
                });

                return schema;
            }

            function setInitialValues(fields: FormField[]) {
                const values: FormValues = {
                    Iata: undefined,
                    Date: undefined,
                    FlightNumber: undefined,
                    FlightTime: undefined,
                };

                fields?.forEach((f) => {
                    if (f) {
                        values[f.name] = f.value;
                    }
                });

                return values;
            }

            function handleSubmit(values: FormValues) {
                if (values.Date && values.FlightTime && values.FlightNumber && values.Iata) {
                    const date = addTimeToDate(values.Date, values.FlightTime);

                    const airport = airports.value.find((x) => x.value === values.Iata);

                    store
                        .dispatch('checkout/setTransferTripAirportData', {
                            id: props.trip.id,
                            [flightData.value.connection]: {
                                description: airport?.label,
                                flightNumber: values.FlightNumber,
                                location: { iata: values.Iata },
                            },
                            request: {
                                [flightData.value.time]: date,
                                [flightData.value.connection]: {
                                    type: SearchLocationType.Iata,
                                    iata: values.Iata,
                                },
                            },
                        })
                        .then(() => props.onSearch());
                }
            }

            watch(
                () => fields.value,
                (newFields, prevFields) => {
                    if (newFields.length !== prevFields?.length) {
                        setInitialState(newFields);
                    }
                },
                { immediate: true }
            );

            return {
                formId,
                fields,
                description,
                validationSchema,
                initialValues,
                messageWarning,
                messageInvalid,
                disabled,
                handleSubmit,
                airports,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './PreSelectionAirport.scss';
</style>
